import { ISendEmailFeatures } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import { baseStyleSheet } from '../../../styles/styles';
import { BusinessHoursModal } from '../../business-hours/BusinessHoursModal';
import { Checkbox } from '../../Checkbox';
import { styleSheet } from '../styles';

interface IProps {
	accountForUpdate?: { companyName?: string };
	onUpdateFormFieldValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onSaveClick: () => void;
	onCancelClick: () => void;
	getField: (value?: string) => string;
}

const CompanySettingsSectionComponent: React.FC<IProps> = ({
	accountForUpdate,
	onUpdateFormFieldValue,
	onSaveClick,
	onCancelClick,
	getField,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const [businessHoursModalOpen, setBusinessHoursModalOpen] = React.useState(false);
	const [savingBusinessHours, setSavingBusinessHours] = React.useState(false);
	const [updatingCheckbox, setUpdatingCheckbox] = React.useState(false);
	const abortControllerRef = React.useRef<AbortController | null>(null);

	// Use local state to prevent flicker - initialize from account state only once
	const [localBusinessHoursEnabled, setLocalBusinessHoursEnabled] = React.useState(
		() => userSession.account.features?.sendEmail?.observeSendIntervals ?? false
	);

	// Cleanup on unmount
	React.useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	const handleBusinessHoursEnabledChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const enabled = e.target.checked;
		// Immediately update local state to prevent flicker
		setLocalBusinessHoursEnabled(enabled);
		setUpdatingCheckbox(true);

		// Cancel any previous request
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		abortControllerRef.current = new AbortController();

		try {
			// Update the server in the background (no toast for simple enable/disable)
			const updatedFeatures = { ...userSession.account.features?.sendEmail, observeSendIntervals: enabled };
			await updateBusinessHours(updatedFeatures, false);
		} catch {
			// Only revert if the request wasn't aborted
			if (!abortControllerRef.current?.signal.aborted) {
				setLocalBusinessHoursEnabled(!enabled);
			}
		} finally {
			if (!abortControllerRef.current?.signal.aborted) {
				setUpdatingCheckbox(false);
			}
		}
	};

	const openBusinessHoursModal = () => {
		setBusinessHoursModalOpen(true);
	};

	const onBusinessHoursModalClose = (sendEmailFeatures?: Partial<ISendEmailFeatures>) => {
		if (sendEmailFeatures) {
			updateBusinessHours(sendEmailFeatures);
		}
		setBusinessHoursModalOpen(false);
	};

	const updateBusinessHours = async (sendEmailFeatures: Partial<ISendEmailFeatures>, showToast = true) => {
		setSavingBusinessHours(true);
		try {
			await userSession.account.updateSendEmailFeatures(sendEmailFeatures);
			// Only show toast for detailed changes (like from the modal), not for simple enable/disable
			if (showToast) {
				setTimeout(() => {
					toast.success('Business hours updated successfully');
				}, 100);
			}
		} catch (error) {
			errorMessages.pushApiError(error);
		} finally {
			setSavingBusinessHours(false);
		}
	};

	return (
		<div className='settings-admin-company-container'>
			<div className={css(baseStyleSheet.mb2)}>
				<span className={css(styleSheet.formFieldLabel)}>Company Name</span>
				<input
					type='text'
					id='editCompanyName'
					placeholder='Company Name (required)'
					className={css(baseStyleSheet.textFieldStandard)}
					value={accountForUpdate ? getField(accountForUpdate.companyName) : ''}
					onChange={onUpdateFormFieldValue}
				/>
			</div>

			<div className={css(baseStyleSheet.mb2, baseStyleSheet.mt4)}>
				<div className={css(styleSheet.formFieldLabel, baseStyleSheet.mb1)}>Business Hours</div>
				<div className={css(styleSheet.businessHoursExplanation)}>
					Business Hours ensure that your email and text campaigns pause at appropriate times so recipients do not
					receive your messages at odd hours. To change your business hours and/or days, click Manage below.{' '}
					<strong>Warning:</strong> Completely disabling Business Hours could cause recipients to get your emails and
					texts at midnight, 3am, etc.
				</div>
				<div
					className={css(styleSheet.businessHoursContainer, styleSheet.businessHoursToggleContainer)}
					style={{ opacity: updatingCheckbox ? 0.7 : 1 }}
				>
					<Checkbox
						checked={localBusinessHoursEnabled}
						onChange={handleBusinessHoursEnabledChanged}
						className={css(styleSheet.customCheckbox)}
						disabled={updatingCheckbox}
					>
						<span className={css(styleSheet.checkboxLabel)}>Enable Business Hours</span>
					</Checkbox>
					{localBusinessHoursEnabled && (
						<button
							className={css(baseStyleSheet.brandLink)}
							onClick={openBusinessHoursModal}
							disabled={updatingCheckbox}
						>
							Manage
						</button>
					)}
				</div>
			</div>

			<div className={css(baseStyleSheet.mb2, baseStyleSheet.mt4, styleSheet.buttonContainer)}>
				<Button variant='primary' size='sm' onClick={onSaveClick}>
					Save
				</Button>
				<Button variant='secondary' size='sm' onClick={onCancelClick}>
					Cancel
				</Button>
			</div>

			<BusinessHoursModal
				isOpen={businessHoursModalOpen}
				isSaving={savingBusinessHours}
				sendEmailFeatures={userSession.account.features?.sendEmail}
				onRequestClose={onBusinessHoursModalClose}
			/>
		</div>
	);
};

// Memoize the component to prevent unnecessary re-renders
export const CompanySettingsSection = React.memo(CompanySettingsSectionComponent);
